import {
  bookingAcceptedTemplate,
  bookingCanceledCustomerTemplate,
  bookingDeclinedTemplate,
  bookingEditedTemplate,
  bookingRegistrationTemplate,
  bookingReminderTemplate,
  bookingAcceptedCommentTemplate,
  bookingCanceledCompanyTemplate,
  bookingCustomMailTemplate,
  bookingRegistrationNotificationTemplate,
  foodReservationNotificationTemplate,
  serviceReservationNotificationTemplate,
  bookingFooterTemplate,
} from '@xeetr/mail/templates'
import { TenantType } from '@xeetr/types/enums'
import { Settings } from '@xeetr/types/models'

export type EmailTemplate = {
  key: string
  name: string
  bodyField: keyof Settings['tenant']['settings']
  defaultSubject: string
  defaultBody: string
  type: 'email' | 'component'
  variables?: Array<{ name: string; description: string }>
  tenantType?: TenantType | 'all'
  recipient?: 'customer' | 'tenant'
}

export const EMAIL_TEMPLATE_CONFIGS: Omit<EmailTemplate, 'defaultBody'>[] = [
  {
    key: 'tenant-booking-accepted',
    name: 'Buchungsbestätigung',
    bodyField: 'customTenantBookingAccepted',
    defaultSubject: 'Deine Reservierung bei {{tenantName}}',
    type: 'email',
    variables: [{ name: '{{comment}}', description: 'Optionaler Kommentar' }],
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-registration',
    name: 'Buchungsanfrage',
    bodyField: 'customTenantBookingRegistration',
    defaultSubject: 'Deine Reservierung bei {{tenantName}} ist eingegangen',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-declined',
    name: 'Buchungsablehnung',
    bodyField: 'customTenantBookingDeclined',
    defaultSubject: 'Deine Reservierung bei {{tenantName}} wurde abgelehnt',
    type: 'email',
    variables: [{ name: '{{reason}}', description: 'Ablehnungsgrund' }],
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-canceled',
    name: 'Stornierungsbestätigung',
    bodyField: 'customTenantBookingCanceled',
    defaultSubject: 'Deine Reservierung bei {{tenantName}} wurde storniert',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-edited',
    name: 'Änderungsbestätigung',
    bodyField: 'customTenantBookingEdited',
    defaultSubject: 'Deine Reservierung bei {{tenantName}} wurde angepasst',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-reminder',
    name: 'Erinnerung',
    bodyField: 'customTenantBookingReminder',
    defaultSubject: 'Erinnerung an deine Reservierung',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-accepted-comment',
    name: 'Buchungsbestätigung Kommentar',
    bodyField: 'customTenantBookingAcceptedComment',
    defaultSubject: 'Kommentar zu deiner Reservierung',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-canceled-company',
    name: 'Stornierungsbestätigung',
    bodyField: 'customTenantBookingCanceledCompany',
    defaultSubject: 'Reservierung storniert',
    type: 'email',
    tenantType: 'all',
    recipient: 'tenant',
  },
  {
    key: 'tenant-booking-custom-mail',
    name: 'Benutzerdefinierte Nachricht',
    bodyField: 'customTenantBookingCustomMail',
    defaultSubject: 'Nachricht zu deiner Reservierung',
    type: 'email',
    tenantType: 'all',
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-registration-notification',
    name: 'Buchungsanfrage',
    bodyField: 'customTenantBookingRegistrationNotification',
    defaultSubject: 'Neue Buchungsanfrage',
    type: 'email',
    tenantType: 'all',
    recipient: 'tenant',
  },
  {
    key: 'tenant-food-reservation-notification',
    name: 'Essensreservierung Benachrichtigung',
    bodyField: 'customTenantFoodReservationNotification',
    defaultSubject: 'Benachrichtigung zu deiner Essensreservierung',
    type: 'component',
    tenantType: TenantType.FOOD,
    recipient: 'customer',
  },
  {
    key: 'tenant-service-reservation-notification',
    name: 'Servicereservierung Benachrichtigung',
    bodyField: 'customTenantServiceReservationNotification',
    defaultSubject: 'Benachrichtigung zu deiner Servicereservierung',
    type: 'component',
    tenantType: TenantType.SERVICE,
    recipient: 'customer',
  },
  {
    key: 'tenant-booking-footer',
    name: 'E-Mail Footer',
    bodyField: 'customTenantBookingFooter',
    defaultSubject: '',
    type: 'component',
    tenantType: 'all',
  },
]

export const EMAIL_TEMPLATE_DEFAULTS: Record<string, string> = {
  'tenant-booking-accepted': bookingAcceptedTemplate,
  'tenant-booking-registration': bookingRegistrationTemplate,
  'tenant-booking-declined': bookingDeclinedTemplate,
  'tenant-booking-canceled': bookingCanceledCustomerTemplate,
  'tenant-booking-edited': bookingEditedTemplate,
  'tenant-booking-reminder': bookingReminderTemplate,
  'tenant-booking-accepted-comment': bookingAcceptedCommentTemplate,
  'tenant-booking-canceled-company': bookingCanceledCompanyTemplate,
  'tenant-booking-custom-mail': bookingCustomMailTemplate,
  'tenant-booking-registration-notification': bookingRegistrationNotificationTemplate,
  'tenant-food-reservation-notification': foodReservationNotificationTemplate,
  'tenant-service-reservation-notification': serviceReservationNotificationTemplate,
  'tenant-booking-footer': bookingFooterTemplate,
}

export const COMMON_EMAIL_VARIABLES = [
  { name: '{{tenantLogo}}', description: 'Logo des Unternehmens' },
  { name: '{{name}}', description: 'Name des Kunden' },
  { name: '{{tenantName}}', description: 'Name des Unternehmens' },
  { name: '{{cancelLink}}', description: 'Link zum Stornieren' },
  { name: '{{tenantSpecificPart}}', description: 'Spezifische Details zur Reservierung' },
]

export const COMPONENT_VARIABLES = [
  { name: '{{booking}}', description: 'Reservierungsdetails' },
  { name: '{{bookingRoom}}', description: 'Raum der Reservierung' },
  { name: '{{customerAmount}}', description: 'Anzahl der Personen' },
  { name: '{{customerAddress}}', description: 'Adresse der Personen' },
  { name: '{{customerAmountDescription}}', description: 'Beschreibung der Personen' },
  { name: '{{service}}', description: 'Service-Details' },
  { name: '{{date}}', description: 'Datum der Reservierung' },
  { name: '{{employee}}', description: 'Mitarbeiter-Details' },
  { name: '{{tenantAddress}}', description: 'Adresse des Unternehmens' },
]

export const PLACEHOLDER_REPLACEMENTS: Record<string, string> = {
  '{{tenantLogo}}': 'https://example.com/logo.png',
  '{{name}}': 'Max Mustermann',
  '{{tenantName}}': 'Test Restaurant',
  '{{cancelLink}}': 'https://example.com/cancel',
  '{{booking}}': 'Dienstag, der 14.10.2025 um 08:30 - 10:30 Uhr',
  '{{bookingRoom}}': 'Beliebiger Raum',
  '{{customerAmount}}': '2',
  '{{customerAmountDescription}}': 'Personen',
  '{{service}}': 'Test Service',
  '{{date}}': 'Dienstag, der 14.10.2025',
  '{{employee}}': 'Test Employee',
  '{{message}}': 'Dies ist eine Testnachricht.',
  '{{reason}}': 'Kapazität nicht verfügbar.',
  '{{footer}}':
    '<p style="margin: 0; font-size: 14px; line-height: 21px; color: #333333"><strong>made with love from xeetr</strong></p><div style="margin: 0; padding-top: 10px; padding-bottom: 10px"><p>© 2025 GeeZee GmbH</p><p>www.xeetr.de - www.geezee-it.de</p></div><div style="display: hidden">12345</div>',
}
